/**
 * Place solar panels on a 3D roof mesh.
 *
 * Map lat/lng positions do not share the mesh frame (the GLB is recentered,
 * rotated, and mirrored). Panels are fitted to detected roof planes instead,
 * and a facet is kept only when it can hold at least two modules.
 */

import { DEFAULT_LAYOUT_CONFIG, type PanelLayoutConfig } from "@/utils/panelLayoutCalculator";

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export type MeshTriangle = [Vec3, Vec3, Vec3];

export interface PlacedRoofPanel {
  position: Vec3;
  /** Outward roof normal. */
  normal: Vec3;
  /** Panel width axis, lying in the roof plane. */
  across: Vec3;
  /** Panel height axis, lying in the roof plane. */
  down: Vec3;
  width: number;
  height: number;
  /** Stable roof facet this module belongs to. */
  facetIndex: number;
}

export interface RoofMeshLayout {
  panels: PlacedRoofPanel[];
  /** Panel counts aligned with facetIndex, in the order the roofs were found. */
  facetCounts: number[];
  totalKwp: number;
  /** Fitted modules sit at or above this mesh-frame height. */
  minPanelY?: number;
}

/** Starting picture on the plan when the roof can hold more than this. */
export const PLAN_PANEL_DISPLAY_CAP = 12;

/**
 * Modules to draw for a package count.
 * Walks the fitted layout in order and skips any module whose plan footprint
 * crosses one already kept, so raising the count never stacks panels.
 */
export function indicesWithinPanelLimit(
  layout: RoofMeshLayout,
  limit: number | null | undefined,
): number[] {
  const total = layout.panels.length;
  if (limit == null || !Number.isFinite(limit)) {
    return layout.panels.map((_, index) => index);
  }
  const count = Math.max(0, Math.floor(limit));
  const kept: number[] = [];
  const footprints: Array<Array<{ x: number; z: number }>> = [];
  for (let index = 0; index < total && kept.length < count; index++) {
    const footprint = planFootprint(layout.panels[index]);
    if (footprints.some((other) => footprintsOverlap(footprint, other))) continue;
    kept.push(index);
    footprints.push(footprint);
  }
  return kept;
}

export function visiblePanelIndexSet(
  layout: RoofMeshLayout,
  limit: number | null | undefined,
  removed: Iterable<number> = [],
  pinned: Iterable<number> = [],
): Set<number> {
  const shown = new Set(indicesWithinPanelLimit(layout, limit));
  for (const index of pinned) {
    if (index >= 0 && index < layout.panels.length) shown.add(index);
  }
  for (const index of removed) shown.delete(index);
  return shown;
}

/** Customer edit state used when the panel stepper resizes instead of resetting. */
export interface PanelVisibilityState {
  removed: number[];
  /** Higher-priority indices stay visible when the count drops. */
  priority: number[];
}

export function roofMeshLayoutForIndices(
  full: RoofMeshLayout,
  indices: Iterable<number>,
): RoofMeshLayout {
  const shown = new Set(indices);
  const panels = full.panels.filter((_, index) => shown.has(index));
  return {
    panels,
    facetCounts: full.facetCounts.map((_, facet) => (
      panels.filter((panel) => panel.facetIndex === facet).length
    )),
    totalKwp: (panels.length * DEFAULT_LAYOUT_CONFIG.panelWatts) / 1000,
  };
}

/** Build priority with visible modules first, then the rest in layout order. */
export function seedPriorityFromShown(
  layout: RoofMeshLayout,
  shown: Set<number>,
): number[] {
  const priority: number[] = [];
  for (let index = 0; index < layout.panels.length; index++) {
    if (shown.has(index)) priority.push(index);
  }
  for (let index = 0; index < layout.panels.length; index++) {
    if (!shown.has(index)) priority.push(index);
  }
  return priority;
}

function panelLimitCount(limit: number | null | undefined, total: number): number {
  if (limit == null || !Number.isFinite(limit)) return total;
  return Math.max(0, Math.floor(limit));
}

function indicesKeptWithOverlap(
  layout: RoofMeshLayout,
  ordered: number[],
  limit: number,
  removed: ReadonlySet<number>,
): number[] {
  const kept: number[] = [];
  const footprints: Array<Array<{ x: number; z: number }>> = [];
  for (const index of ordered) {
    if (kept.length >= limit) break;
    if (index < 0 || index >= layout.panels.length || removed.has(index)) continue;
    const footprint = planFootprint(layout.panels[index]);
    if (footprints.some((other) => footprintsOverlap(footprint, other))) continue;
    kept.push(index);
    footprints.push(footprint);
  }
  return kept;
}

/**
 * Show `targetCount` modules from a remembered arrangement.
 * The current order is kept, so a moved or rotated module stays put.
 * A lower count drops the tail. A higher count brings hidden modules back,
 * then unused fitted modules, skipping any the customer deleted.
 */
export function clipRememberedVisibility(
  panelCount: number,
  targetCount: number | null | undefined,
  state: PanelVisibilityState & { shown?: number[] },
): PanelVisibilityState & { shown: number[] } {
  const limit = panelLimitCount(targetCount, panelCount);
  const removed = new Set(
    [...state.removed].filter((index) => index >= 0 && index < panelCount),
  );
  const prefer = (state.shown?.length ? state.shown : state.priority).filter(
    (index) => index >= 0 && index < panelCount && !removed.has(index),
  );
  const shown: number[] = [];
  const seen = new Set<number>();
  const take = (index: number) => {
    if (shown.length >= limit || seen.has(index) || removed.has(index)) return;
    if (index < 0 || index >= panelCount) return;
    shown.push(index);
    seen.add(index);
  };
  for (const index of prefer) take(index);
  if (shown.length < limit) {
    for (const index of state.priority) take(index);
    for (let index = 0; index < panelCount; index++) take(index);
  }
  const hidden = state.priority.filter((index) => !seen.has(index) && !removed.has(index));
  for (let index = 0; index < panelCount; index++) {
    if (!seen.has(index) && !removed.has(index) && !hidden.includes(index)) hidden.push(index);
  }
  return {
    removed: [...removed],
    priority: [...shown, ...hidden],
    shown,
  };
}

/**
 * Choose which modules stay visible after the customer has edited the roof.
 * Walks `priority` first, then any remaining non-removed indices.
 */
export function resizeVisiblePanels(
  layout: RoofMeshLayout,
  targetCount: number | null | undefined,
  state: PanelVisibilityState,
): PanelVisibilityState & { shown: Set<number>; pinned: number[] } {
  const limit = panelLimitCount(targetCount, layout.panels.length);
  const removedSet = new Set(state.removed);
  const seen = new Set<number>();
  const ordered: number[] = [];
  for (const index of state.priority) {
    if (index >= 0 && index < layout.panels.length && !seen.has(index)) {
      ordered.push(index);
      seen.add(index);
    }
  }
  for (let index = 0; index < layout.panels.length; index++) {
    if (!seen.has(index)) ordered.push(index);
  }

  const kept = indicesKeptWithOverlap(layout, ordered, limit, removedSet);
  const shown = new Set(kept);
  const baseline = new Set(indicesWithinPanelLimit(layout, targetCount));
  const pinned = kept.filter((index) => !baseline.has(index));

  const keptOrder = state.priority.filter((index) => shown.has(index));
  const hiddenPriority = state.priority.filter(
    (index) => !shown.has(index) && !removedSet.has(index),
  );
  const hiddenRest: number[] = [];
  for (let index = 0; index < layout.panels.length; index++) {
    if (!shown.has(index) && !removedSet.has(index) && !state.priority.includes(index)) {
      hiddenRest.push(index);
    }
  }

  return {
    removed: [...state.removed],
    priority: [...keptOrder, ...hiddenPriority, ...hiddenRest],
    shown,
    pinned,
  };
}

/**
 * Keep up to `limit` modules in placement order, leaving out any that would overlap.
 * A missing limit leaves the layout unchanged.
 */
export function layoutWithPanelLimit(
  layout: RoofMeshLayout,
  limit: number | null | undefined,
): RoofMeshLayout {
  const indices = indicesWithinPanelLimit(layout, limit);
  if (indices.length === layout.panels.length && indices.every((index, position) => index === position)) {
    return layout;
  }
  const panels = indices.map((index) => layout.panels[index]);
  return {
    panels,
    facetCounts: layout.facetCounts.map((_, facet) => (
      panels.filter((panel) => panel.facetIndex === facet).length
    )),
    totalKwp: (panels.length * DEFAULT_LAYOUT_CONFIG.panelWatts) / 1000,
  };
}

/** Plan-view corners, using the same axes as the 3D module. */
function planFootprint(panel: PlacedRoofPanel): Array<{ x: number; z: number }> {
  const normal = normalize(panel.normal);
  const across = normalize(panel.across);
  const down = normalize(cross(across, normal));
  const halfWidth = panel.width / 2;
  const halfHeight = panel.height / 2;
  return [
    [1, 1],
    [-1, 1],
    [-1, -1],
    [1, -1],
  ].map(([acrossSign, downSign]) => ({
    x: panel.position.x + across.x * halfWidth * acrossSign + down.x * halfHeight * downSign,
    z: panel.position.z + across.z * halfWidth * acrossSign + down.z * halfHeight * downSign,
  }));
}

const MIN_NORMAL_Y = 0.42;
/** Ignore roof geometry this far above the lowest point of the mesh. */
export const MIN_PANEL_CLEARANCE_M = 4;

export function panelFloorY(triangles: MeshTriangle[]): number {
  let minY = Infinity;
  for (const [a, b, c] of triangles) {
    minY = Math.min(minY, a.y, b.y, c.y);
  }
  if (!Number.isFinite(minY)) return -Infinity;
  return minY + MIN_PANEL_CLEARANCE_M;
}
const NORMAL_DOT = 0.94;
const PLANE_TOLERANCE_M = 0.45;
/** Click must fall this close to a grid node, as a fraction of the cell step. */
const SNAP_NODE_FRACTION = 0.35;
/** A module already on this node, within a few centimetres. */
const LATTICE_NODE_M = 0.08;
const LATTICE_AXIS_DOT = 0.98;

export interface SnappedRoofPanel {
  panel: PlacedRoofPanel;
  /** Fitted module already on this cell. Null when the cell is new. */
  existingIndex: number | null;
}

/**
 * Snap a roof click to the next cell of a nearby visible module.
 * The cell must sit on that module's grid and share an edge with one.
 * A hidden module already on the cell is returned so it can be shown again.
 */
export function snapPanelBesideGrid(
  hit: Vec3,
  hitNormal: Vec3,
  panels: PlacedRoofPanel[],
  visible: Iterable<number>,
  gap = DEFAULT_LAYOUT_CONFIG.panelGap,
  minY = -Infinity,
): SnappedRoofPanel | null {
  const hitFacing = normalize(hitNormal);
  if (hitFacing.y < MIN_NORMAL_Y) return null;
  if (hit.y < minY) return null;

  const visibleSet = new Set<number>();
  for (const index of visible) {
    if (index >= 0 && index < panels.length) visibleSet.add(index);
  }
  if (visibleSet.size === 0) return null;

  let anchorIndex = -1;
  let anchorDistance = Infinity;
  for (const index of visibleSet) {
    const panel = panels[index];
    const panelNormal = normalize(panel.normal);
    if (dot(panelNormal, hitFacing) < NORMAL_DOT) continue;
    const lift = Math.abs(dot(sub(hit, panel.position), panelNormal));
    if (lift > PLANE_TOLERANCE_M) continue;
    const distance = length(sub(hit, panel.position));
    if (distance < anchorDistance) {
      anchorDistance = distance;
      anchorIndex = index;
    }
  }
  if (anchorIndex < 0) return null;

  const anchor = panels[anchorIndex];
  const across = normalize(anchor.across);
  const down = normalize(anchor.down);
  const planeNormal = normalize(anchor.normal);
  const stepU = anchor.width + gap;
  const stepV = anchor.height + gap;
  if (stepU <= gap || stepV <= gap) return null;

  const offset = sub(hit, anchor.position);
  const u = dot(offset, across);
  const v = dot(offset, down);
  const col = Math.round(u / stepU);
  const row = Math.round(v / stepV);
  if (Math.abs(u - col * stepU) > SNAP_NODE_FRACTION * stepU) return null;
  if (Math.abs(v - row * stepV) > SNAP_NODE_FRACTION * stepV) return null;

  const adjacent = [...visibleSet].some((index) => {
    const cell = latticeCell(panels[index], anchor, across, down, planeNormal, stepU, stepV);
    if (!cell) return false;
    const dCol = Math.abs(cell.col - col);
    const dRow = Math.abs(cell.row - row);
    return dCol + dRow === 1;
  });
  if (!adjacent) return null;

  const position = add(
    anchor.position,
    add(scale(across, col * stepU), scale(down, row * stepV)),
  );
  const posed: PlacedRoofPanel = {
    position,
    normal: planeNormal,
    across,
    down,
    width: anchor.width,
    height: anchor.height,
    facetIndex: anchor.facetIndex,
  };

  let existingIndex: number | null = null;
  for (let index = 0; index < panels.length; index++) {
    const cell = latticeCell(panels[index], anchor, across, down, planeNormal, stepU, stepV);
    if (cell && cell.col === col && cell.row === row) {
      existingIndex = index;
      break;
    }
  }

  const pose = existingIndex != null ? panels[existingIndex] : posed;
  const footprint = planFootprint(pose);
  for (const index of visibleSet) {
    if (index === existingIndex) continue;
    if (footprintsOverlap(footprint, planFootprint(panels[index]))) return null;
  }
  if (existingIndex != null) {
    if (visibleSet.has(existingIndex)) return null;
    if (panels[existingIndex].position.y < minY) return null;
    return { panel: panels[existingIndex], existingIndex };
  }

  let hiddenIndex: number | null = null;
  let hiddenDistance = Infinity;
  for (let index = 0; index < panels.length; index++) {
    if (visibleSet.has(index)) continue;
    if (!footprintsOverlap(footprint, planFootprint(panels[index]))) continue;
    const distance = length(sub(panels[index].position, pose.position));
    if (distance < hiddenDistance) {
      hiddenDistance = distance;
      hiddenIndex = index;
    }
  }
  if (hiddenIndex != null) {
    if (panels[hiddenIndex].position.y < minY) return null;
    return { panel: panels[hiddenIndex], existingIndex: hiddenIndex };
  }
  if (posed.position.y < minY) return null;
  return { panel: posed, existingIndex: null };
}

export function isRoofFacingNormal(normal: Vec3): boolean {
  return normal.y >= MIN_NORMAL_Y;
}

function latticeCell(
  panel: PlacedRoofPanel,
  anchor: PlacedRoofPanel,
  across: Vec3,
  down: Vec3,
  planeNormal: Vec3,
  stepU: number,
  stepV: number,
): { col: number; row: number } | null {
  if (panel.facetIndex !== anchor.facetIndex) return null;
  if (dot(normalize(panel.normal), planeNormal) < LATTICE_AXIS_DOT) return null;
  if (dot(normalize(panel.across), across) < LATTICE_AXIS_DOT) return null;
  const offset = sub(panel.position, anchor.position);
  if (Math.abs(dot(offset, planeNormal)) > LATTICE_NODE_M) return null;
  const u = dot(offset, across);
  const v = dot(offset, down);
  const col = Math.round(u / stepU);
  const row = Math.round(v / stepV);
  if (Math.abs(u - col * stepU) > LATTICE_NODE_M) return null;
  if (Math.abs(v - row * stepV) > LATTICE_NODE_M) return null;
  return { col, row };
}

/** True when two convex footprints cover the same ground, not merely touch. */
function footprintsOverlap(
  a: Array<{ x: number; z: number }>,
  b: Array<{ x: number; z: number }>,
): boolean {
  const axes = [...edgeAxes(a), ...edgeAxes(b)];
  return axes.every((axis) => {
    const [a0, a1] = projectOnto(a, axis);
    const [b0, b1] = projectOnto(b, axis);
    return a1 > b0 + 0.001 && b1 > a0 + 0.001;
  });
}

function edgeAxes(quad: Array<{ x: number; z: number }>): Array<{ x: number; z: number }> {
  return quad.map((point, index) => {
    const next = quad[(index + 1) % quad.length];
    return { x: -(next.z - point.z), z: next.x - point.x };
  });
}

function projectOnto(
  quad: Array<{ x: number; z: number }>,
  axis: { x: number; z: number },
): [number, number] {
  let min = Infinity;
  let max = -Infinity;
  for (const point of quad) {
    const value = point.x * axis.x + point.z * axis.z;
    min = Math.min(min, value);
    max = Math.max(max, value);
  }
  return [min, max];
}

const CELL_M = 0.25;
const MERGE_GAP_M = 1.2;
const COVERAGE = 0.8;
/** A stack or dormer cheek this far above the fitted roof plane is kept clear. */
const PEAK_ABOVE_PLANE_M = 0.35;
const PEAK_MARGIN_M = 0.4;

export type PanelMount = "portrait" | "landscape";

export function placePanelsOnRoofTriangles(
  triangles: MeshTriangle[],
  config: PanelLayoutConfig = DEFAULT_LAYOUT_CONFIG,
  mount: PanelMount = "portrait",
): RoofMeshLayout {
  const floorY = panelFloorY(triangles);
  const samples = roofSamples(triangles);
  const cells = buildHeightCells(samples);
  const peaks = surfacePeaks(triangles);
  const regions = mergeCoplanar(growRegions(cells));
  const panels: PlacedRoofPanel[] = [];
  const facetCounts: number[] = [];

  for (const region of regions) {
    const placed = packFacet(fillRegionHoles(region), config, mount, peaks)
      .filter((panel) => panel.position.y >= floorY);
    if (placed.length < 2) continue;
    const facetIndex = facetCounts.length;
    facetCounts.push(placed.length);
    panels.push(...placed.map((panel) => ({ ...panel, facetIndex })));
  }

  const totalKwp = (panels.length * config.panelWatts) / 1000;
  return { panels, facetCounts, totalKwp, minPanelY: floorY };
}

interface Sample {
  x: number;
  y: number;
  z: number;
}

interface Cell {
  x: number;
  y: number;
  z: number;
  nx: number;
  ny: number;
  nz: number;
  ix: number;
  iz: number;
}

function roofSamples(triangles: MeshTriangle[]): Sample[] {
  if (triangles.length === 0) return [];
  const floorY = panelFloorY(triangles);
  const samples: Sample[] = [];
  for (const [a, b, c] of triangles) {
    const ab = sub(b, a);
    const ac = sub(c, a);
    const n = cross(ab, ac);
    const len = length(n);
    if (len < 1e-8) continue;
    const ny = Math.abs(n.y / len);
    if (ny < MIN_NORMAL_Y) continue;
    const centroid = {
      x: (a.x + b.x + c.x) / 3,
      y: (a.y + b.y + c.y) / 3,
      z: (a.z + b.z + c.z) / 3,
    };
    if (centroid.y < floorY) continue;
    samples.push(centroid);
  }
  return samples;
}

function buildHeightCells(samples: Sample[]): Cell[] {
  const buckets = new Map<string, { x: number; y: number; z: number; n: number }>();
  for (const sample of samples) {
    const ix = Math.round(sample.x / CELL_M);
    const iz = Math.round(sample.z / CELL_M);
    const key = `${ix},${iz}`;
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.x += sample.x;
      bucket.y += sample.y;
      bucket.z += sample.z;
      bucket.n += 1;
    } else {
      buckets.set(key, { x: sample.x, y: sample.y, z: sample.z, n: 1 });
    }
  }

  const height = new Map<string, { x: number; y: number; z: number; ix: number; iz: number }>();
  for (const [key, bucket] of buckets) {
    const [ix, iz] = key.split(",").map(Number);
    height.set(key, {
      x: bucket.x / bucket.n,
      y: bucket.y / bucket.n,
      z: bucket.z / bucket.n,
      ix,
      iz,
    });
  }

  const cells: Cell[] = [];
  for (const point of height.values()) {
    const normal = heightNormal(height, point.ix, point.iz);
    if (!normal || normal.y < MIN_NORMAL_Y) continue;
    cells.push({ ...point, nx: normal.x, ny: normal.y, nz: normal.z });
  }
  return cells;
}

function heightNormal(
  height: Map<string, { y: number }>,
  ix: number,
  iz: number,
): Vec3 | null {
  const y = (x: number, z: number) => height.get(`${x},${z}`)?.y;
  const left = y(ix - 1, iz);
  const right = y(ix + 1, iz);
  const back = y(ix, iz - 1);
  const forward = y(ix, iz + 1);
  let dhdx: number | null = null;
  let dhdz: number | null = null;
  if (left !== undefined && right !== undefined) dhdx = (right - left) / (2 * CELL_M);
  else if (right !== undefined && y(ix, iz) !== undefined) dhdx = (right - y(ix, iz)!) / CELL_M;
  else if (left !== undefined && y(ix, iz) !== undefined) dhdx = (y(ix, iz)! - left) / CELL_M;
  if (back !== undefined && forward !== undefined) dhdz = (forward - back) / (2 * CELL_M);
  else if (forward !== undefined && y(ix, iz) !== undefined) dhdz = (forward - y(ix, iz)!) / CELL_M;
  else if (back !== undefined && y(ix, iz) !== undefined) dhdz = (y(ix, iz)! - back) / CELL_M;
  if (dhdx === null || dhdz === null) return null;
  return normalize({ x: -dhdx, y: 1, z: -dhdz });
}

function growRegions(cells: Cell[]): Cell[][] {
  const byKey = new Map<string, Cell>();
  for (const cell of cells) byKey.set(`${cell.ix},${cell.iz}`, cell);
  const visited = new Set<string>();
  const order = [...cells].sort((a, b) => curvature(byKey, a) - curvature(byKey, b));
  const regions: Cell[][] = [];

  for (const seed of order) {
    const seedKey = `${seed.ix},${seed.iz}`;
    if (visited.has(seedKey)) continue;
    const region: Cell[] = [];
    const queue = [seed];
    visited.add(seedKey);
    const normal = { x: seed.nx, y: seed.ny, z: seed.nz };
    const origin = { x: seed.x, y: seed.y, z: seed.z };

    while (queue.length > 0) {
      const current = queue.pop()!;
      region.push(current);
      for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        const next = byKey.get(`${current.ix + dx},${current.iz + dz}`);
        if (!next) continue;
        const key = `${next.ix},${next.iz}`;
        if (visited.has(key)) continue;
        const dot = next.nx * normal.x + next.ny * normal.y + next.nz * normal.z;
        if (dot < NORMAL_DOT) continue;
        const dist = (next.x - origin.x) * normal.x
          + (next.y - origin.y) * normal.y
          + (next.z - origin.z) * normal.z;
        if (Math.abs(dist) > PLANE_TOLERANCE_M) continue;
        visited.add(key);
        queue.push(next);
      }
    }

    if (region.length * CELL_M * CELL_M >= 2) regions.push(region);
  }

  return regions;
}

function curvature(byKey: Map<string, Cell>, cell: Cell): number {
  let score = 0;
  let count = 0;
  for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
    const next = byKey.get(`${cell.ix + dx},${cell.iz + dz}`);
    if (!next) continue;
    score += 1 - (cell.nx * next.nx + cell.ny * next.ny + cell.nz * next.nz);
    count += 1;
  }
  return count === 0 ? 1 : score / count;
}

function mergeCoplanar(regions: Cell[][]): Cell[][] {
  const pending = regions.map((cells) => ({ cells }));
  const merged: Cell[][] = [];
  const used = new Set<number>();

  for (let i = 0; i < pending.length; i++) {
    if (used.has(i)) continue;
    let cells = pending[i].cells;
    used.add(i);
    let grew = true;
    while (grew) {
      grew = false;
      const plane = fitPlane(cells);
      for (let j = 0; j < pending.length; j++) {
        if (used.has(j)) continue;
        const other = fitPlane(pending[j].cells);
        const dot = plane.normal.x * other.normal.x
          + plane.normal.y * other.normal.y
          + plane.normal.z * other.normal.z;
        if (dot < 0.96) continue;
        const gap = Math.abs(
          (other.origin.x - plane.origin.x) * plane.normal.x
          + (other.origin.y - plane.origin.y) * plane.normal.y
          + (other.origin.z - plane.origin.z) * plane.normal.z,
        );
        if (gap > PLANE_TOLERANCE_M) continue;
        if (!regionsNear(cells, pending[j].cells)) continue;
        cells = cells.concat(pending[j].cells);
        used.add(j);
        grew = true;
      }
    }
    merged.push(cells);
  }
  return merged;
}

function regionsNear(a: Cell[], b: Cell[]): boolean {
  const keys = new Set(a.map((cell) => `${cell.ix},${cell.iz}`));
  const reach = Math.ceil(MERGE_GAP_M / CELL_M);
  for (const cell of b) {
    for (let dx = -reach; dx <= reach; dx++) {
      for (let dz = -reach; dz <= reach; dz++) {
        if (keys.has(`${cell.ix + dx},${cell.iz + dz}`)) return true;
      }
    }
  }
  return false;
}

interface Plane {
  origin: Vec3;
  normal: Vec3;
  across: Vec3;
  down: Vec3;
}

function fitPlane(cells: Cell[]): Plane {
  const origin = {
    x: average(cells.map((cell) => cell.x)),
    y: average(cells.map((cell) => cell.y)),
    z: average(cells.map((cell) => cell.z)),
  };
  const normal = normalize({
    x: average(cells.map((cell) => cell.nx)),
    y: average(cells.map((cell) => cell.ny)),
    z: average(cells.map((cell) => cell.nz)),
  });
  let across = normalize(cross({ x: 0, y: 1, z: 0 }, normal));
  if (length(across) < 0.2) across = { x: 1, y: 0, z: 0 };
  // across × normal = down, so the panel's thin axis (local Y) is the roof normal.
  let down = normalize(cross(across, normal));
  if (down.y > 0) {
    down = scale(down, -1);
    across = scale(across, -1);
  }
  return { origin, normal, across, down };
}

function fillRegionHoles(cells: Cell[]): Cell[] {
  const keys = new Map(cells.map((cell) => [`${cell.ix},${cell.iz}`, cell]));
  const minX = Math.min(...cells.map((cell) => cell.ix));
  const maxX = Math.max(...cells.map((cell) => cell.ix));
  const minZ = Math.min(...cells.map((cell) => cell.iz));
  const maxZ = Math.max(...cells.map((cell) => cell.iz));
  const filled = [...cells];
  for (let ix = minX; ix <= maxX; ix++) {
    for (let iz = minZ; iz <= maxZ; iz++) {
      if (keys.has(`${ix},${iz}`)) continue;
      const neighbors = [[1, 0], [-1, 0], [0, 1], [0, -1]]
        .map(([dx, dz]) => keys.get(`${ix + dx},${iz + dz}`))
        .filter((cell): cell is Cell => Boolean(cell));
      if (neighbors.length < 3) continue;
      const cell: Cell = {
        ix,
        iz,
        x: average(neighbors.map((neighbor) => neighbor.x)),
        y: average(neighbors.map((neighbor) => neighbor.y)),
        z: average(neighbors.map((neighbor) => neighbor.z)),
        nx: average(neighbors.map((neighbor) => neighbor.nx)),
        ny: average(neighbors.map((neighbor) => neighbor.ny)),
        nz: average(neighbors.map((neighbor) => neighbor.nz)),
      };
      const normal = normalize({ x: cell.nx, y: cell.ny, z: cell.nz });
      cell.nx = normal.x;
      cell.ny = normal.y;
      cell.nz = normal.z;
      keys.set(`${ix},${iz}`, cell);
      filled.push(cell);
    }
  }
  return filled;
}

function surfacePeaks(triangles: MeshTriangle[]): Sample[] {
  if (triangles.length === 0) return [];
  const floorY = panelFloorY(triangles);
  const buckets = new Map<string, Sample>();
  const consider = (point: Vec3) => {
    if (point.y < floorY) return;
    const ix = Math.round(point.x / CELL_M);
    const iz = Math.round(point.z / CELL_M);
    const key = `${ix},${iz}`;
    const existing = buckets.get(key);
    if (!existing || point.y > existing.y) {
      buckets.set(key, { x: point.x, y: point.y, z: point.z });
    }
  };
  for (const [a, b, c] of triangles) {
    consider(a);
    consider(b);
    consider(c);
  }
  return [...buckets.values()];
}

function obstaclesOnPlane(cells: Cell[], plane: Plane, peaks: Sample[]): Array<{ u: number; v: number }> {
  const keys = new Set(cells.map((cell) => `${cell.ix},${cell.iz}`));
  const reach = 2;
  const obstacles: Array<{ u: number; v: number }> = [];
  for (const peak of peaks) {
    const ix = Math.round(peak.x / CELL_M);
    const iz = Math.round(peak.z / CELL_M);
    let near = false;
    for (let dx = -reach; dx <= reach && !near; dx++) {
      for (let dz = -reach; dz <= reach; dz++) {
        if (keys.has(`${ix + dx},${iz + dz}`)) near = true;
      }
    }
    if (!near) continue;
    const rel = sub(peak, plane.origin);
    if (dot(rel, plane.normal) < PEAK_ABOVE_PLANE_M) continue;
    obstacles.push({ u: dot(rel, plane.across), v: dot(rel, plane.down) });
  }
  return obstacles;
}

function packFacet(
  cells: Cell[],
  config: PanelLayoutConfig,
  mount: PanelMount,
  peaks: Sample[],
): PlacedRoofPanel[] {
  const plane = fitPlane(cells);
  const projected = cells.map((cell) => {
    const rel = sub(cell, plane.origin);
    return {
      u: dot(rel, plane.across),
      v: dot(rel, plane.down),
      lift: dot(rel, plane.normal),
    };
  });
  const minU = Math.min(...projected.map((p) => p.u));
  const maxU = Math.max(...projected.map((p) => p.u));
  const minV = Math.min(...projected.map((p) => p.v));
  const maxV = Math.max(...projected.map((p) => p.v));
  const setback = Math.max(config.edgeSetback, config.ridgeSetback, config.eavesSetback);

  const orientation = mount === "landscape"
    ? { width: config.panelHeight, height: config.panelWidth }
    : { width: config.panelWidth, height: config.panelHeight };
  const orientations = [orientation];
  const obstacles = obstaclesOnPlane(cells, plane, peaks);
  const roofLift = percentile(projected.map((point) => point.lift), 0.5);
  for (const point of projected) {
    if (point.lift > roofLift + PEAK_ABOVE_PLANE_M) obstacles.push({ u: point.u, v: point.v });
  }
  let best: PlacedRoofPanel[] = [];

  for (const orientation of orientations) {
    const stepU = orientation.width + config.panelGap;
    const stepV = orientation.height + config.panelGap;
    const usableU = maxU - minU - 2 * setback;
    const usableV = maxV - minV - 2 * setback;
    if (usableU < orientation.width || usableV < orientation.height) continue;
    const countU = Math.floor((usableU + config.panelGap) / stepU);
    const countV = Math.floor((usableV + config.panelGap) / stepV);
    const usedU = countU * orientation.width + (countU - 1) * config.panelGap;
    const usedV = countV * orientation.height + (countV - 1) * config.panelGap;
    const baseU = minU + setback + (usableU - usedU) / 2 + orientation.width / 2;
    const baseV = minV + setback + (usableV - usedV) / 2 + orientation.height / 2;

    for (const shiftU of [0, CELL_M, -CELL_M]) {
      for (const shiftV of [0, CELL_M, -CELL_M]) {
        const placed: PlacedRoofPanel[] = [];
        for (let row = 0; row < countV; row++) {
          for (let col = 0; col < countU; col++) {
            const u = baseU + shiftU + col * stepU;
            const v = baseV + shiftV + row * stepV;
            const hits = projected.filter(
              (point) => Math.abs(point.u - u) <= orientation.width / 2
                && Math.abs(point.v - v) <= orientation.height / 2,
            );
            const cellArea = (CELL_M * CELL_M) / Math.max(0.4, plane.normal.y);
            const covered = hits.length * cellArea;
            if (covered < COVERAGE * orientation.width * orientation.height) continue;
            const hitsPeak = obstacles.some((obstacle) =>
              Math.abs(obstacle.u - u) <= orientation.width / 2 + PEAK_MARGIN_M
              && Math.abs(obstacle.v - v) <= orientation.height / 2 + PEAK_MARGIN_M,
            );
            if (hitsPeak) continue;
            const lift = Math.max(0, percentile(hits.map((hit) => hit.lift), 0.9));
            // Box is 4cm thick and centered, so this leaves the underside about 6cm clear of the roof.
            const clearance = Math.min(0.22, lift + 0.02 + 0.06);
            const position = add(
              plane.origin,
              add(
                add(scale(plane.across, u), scale(plane.down, v)),
                scale(plane.normal, clearance),
              ),
            );
            placed.push({
              position,
              normal: plane.normal,
              across: plane.across,
              down: plane.down,
              width: orientation.width,
              height: orientation.height,
              facetIndex: 0,
            });
          }
        }
        if (placed.length > best.length) best = placed;
      }
    }
  }

  return best;
}

/** Panels on one facet that share a course across the slope. */
export function panelRows(panels: PlacedRoofPanel[]): number[][] {
  const rows: number[][] = [];
  const used = new Set<number>();
  for (let index = 0; index < panels.length; index++) {
    if (used.has(index)) continue;
    const seed = panels[index];
    const row = [index];
    used.add(index);
    for (let other = index + 1; other < panels.length; other++) {
      if (used.has(other)) continue;
      const panel = panels[other];
      if (panel.facetIndex !== seed.facetIndex) continue;
      if (dot(seed.normal, panel.normal) < 0.98) continue;
      const alongSlope = Math.abs(dot(sub(panel.position, seed.position), seed.down));
      if (alongSlope > seed.height * 0.45) continue;
      row.push(other);
      used.add(other);
    }
    rows.push(row);
  }
  return rows;
}

/**
 * Yaw a row around the roof normal through its centre.
 * Pitch and roll stay fixed, so the modules remain flush with the facet.
 */
export function rotatePanelRow(
  panels: PlacedRoofPanel[],
  indices: number[],
  radians: number,
): PlacedRoofPanel[] {
  if (indices.length === 0 || Math.abs(radians) < 1e-8) return panels;
  const members = indices.map((index) => panels[index]);
  const normal = normalize(members[0].normal);
  const center = {
    x: average(members.map((panel) => panel.position.x)),
    y: average(members.map((panel) => panel.position.y)),
    z: average(members.map((panel) => panel.position.z)),
  };
  const selected = new Set(indices);
  return panels.map((panel, index) => {
    if (!selected.has(index)) return panel;
    return {
      ...panel,
      position: add(center, rotateAroundAxis(sub(panel.position, center), normal, radians)),
      across: normalize(rotateAroundAxis(panel.across, normal, radians)),
      down: normalize(rotateAroundAxis(panel.down, normal, radians)),
    };
  });
}

/** Slide a row along its roof plane. Across follows the row; down follows the slope. */
export function translatePanelRow(
  panels: PlacedRoofPanel[],
  indices: number[],
  alongAcross: number,
  alongDown: number,
): PlacedRoofPanel[] {
  if (indices.length === 0 || (alongAcross === 0 && alongDown === 0)) return panels;
  const seed = panels[indices[0]];
  const delta = add(scale(normalize(seed.across), alongAcross), scale(normalize(seed.down), alongDown));
  const selected = new Set(indices);
  return panels.map((panel, index) => (
    selected.has(index) ? { ...panel, position: add(panel.position, delta) } : panel
  ));
}

function rotateAroundAxis(vector: Vec3, axis: Vec3, radians: number): Vec3 {
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const turned = cross(axis, vector);
  const along = dot(axis, vector);
  return {
    x: vector.x * cos + turned.x * sin + axis.x * along * (1 - cos),
    y: vector.y * cos + turned.y * sin + axis.y * along * (1 - cos),
    z: vector.z * cos + turned.z * sin + axis.z * along * (1 - cos),
  };
}

function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const index = Math.min(sorted.length - 1, Math.max(0, Math.ceil(p * sorted.length) - 1));
  return sorted[index];
}

function average(values: number[]): number {
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function sub(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

function add(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}

function scale(a: Vec3, s: number): Vec3 {
  return { x: a.x * s, y: a.y * s, z: a.z * s };
}

function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}

function length(a: Vec3): number {
  return Math.hypot(a.x, a.y, a.z);
}

function normalize(a: Vec3): Vec3 {
  const len = length(a);
  if (len < 1e-8) return { x: 0, y: 0, z: 0 };
  return scale(a, 1 / len);
}

/** Build a sloped rectangular roof facet as a triangle mesh. Slope length is along +horizontal. */
export function slopedFacetTriangles(
  origin: Vec3,
  across: Vec3,
  horizontalDown: Vec3,
  pitchDegrees: number,
  acrossLength: number,
  slopeLength: number,
  step = 0.3,
): MeshTriangle[] {
  const pitch = (pitchDegrees * Math.PI) / 180;
  const point = (u: number, s: number): Vec3 => ({
    x: origin.x + across.x * u + horizontalDown.x * s * Math.cos(pitch),
    y: origin.y - s * Math.sin(pitch),
    z: origin.z + across.z * u + horizontalDown.z * s * Math.cos(pitch),
  });
  const triangles: MeshTriangle[] = [];
  for (let u = -acrossLength / 2; u < acrossLength / 2 - 1e-6; u += step) {
    const u2 = Math.min(u + step, acrossLength / 2);
    for (let s = 0; s < slopeLength - 1e-6; s += step) {
      const s2 = Math.min(s + step, slopeLength);
      triangles.push([point(u, s), point(u2, s), point(u, s2)]);
      triangles.push([point(u2, s), point(u2, s2), point(u, s2)]);
    }
  }
  return triangles;
}
